import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import DoneIcon from '@mui/icons-material/Done';
import { Box } from '@mui/material';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import { useCvBuilderData } from '../data/CvBuilderDataContext';

/**
 * Quiet, two-state reassurance about persistence.
 *
 * Both flows already autosave on every forward navigation and every list edit — the
 * authenticated one to IndexedDB, the guest one to `PUT /guest/curriculumVitaes` with
 * debounce, single-flight and session recovery — and none of it was ever visible.
 *
 * The failure state matters as much as the success one: pro-core validates the whole CV
 * payload on every guest autosave, so one bad entry rejects the entire save, and
 * `useGuestAutosave` logs that as noise. Without this, the talent keeps typing into a form
 * that stopped saving.
 */
export const SavedIndicator = () => {
  const { lastSavedAt, saveFailed } = useCvBuilderData();

  if (saveFailed) {
    return (
      <Box
        data-testid="save-indicator-failed"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
      >
        <CloudOffOutlinedIcon sx={{ fontSize: 14, color: 'error.main' }} />
        <Text variant="caption" color="error.main">
          {getTrans('cvBuilderPage.saved.failed')}
        </Text>
      </Box>
    );
  }

  if (!lastSavedAt) {
    return null; // nothing persisted yet — don't claim anything
  }

  return (
    <Box
      data-testid="save-indicator-saved"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
    >
      <DoneIcon sx={{ fontSize: 14, color: 'success.main' }} />
      <Text variant="caption" color="success.main">
        {getTrans('cvBuilderPage.saved.label')}
      </Text>
    </Box>
  );
};
